import type { RequestFn } from '../http';

export type SoftwareUpdateStatus = 'UNKNOWN' | 'UP_TO_DATE' | 'UPDATE_AVAILABLE';

// 'kian_deep' setzt das Backend per Auto-Upgrade (versionCrawler) und akzeptiert es
// im Schema (maintenance.ts: z.enum(['manuell','kian','kian_deep'])). Ohne diesen
// Member behandelt TS alle `=== 'kian_deep'`-Checks als toten Code, obwohl sie zur
// Laufzeit greifen (Deep-Check-Vorschau etc.).
export type UpdateSourceCheckMethod = 'manuell' | 'kian' | 'kian_deep';

export interface UpdateSource {
  id: string;
  name: string;
  url: string;
  description: string | null;
  checkIntervalHours: number | null;
  checkMethod: UpdateSourceCheckMethod;
  lastCheckedAt: string | null;
  latestVersionDetected: string | null;
  lastCheckError: string | null;
  loginUrl: string | null;
  loginUsername: string | null;
  hasLoginCredentials: boolean;
  kianProLastCheckedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCheckLog {
  id: string;
  sourceId: string;
  checkedAt: string;
  version: string | null;
  error: string | null;
  method: 'auto' | 'manual';
}

export interface MaintenanceSoftware {
  id: string;
  maintenanceCustomerId: string;
  name: string;
  currentVersion: string | null;
  /** Vom Backend berechnet: currentVersion vs. updateSource.latestVersionDetected */
  status: SoftwareUpdateStatus;
  notes: string | null;
  updateSourceId: string | null;
  updateSource: { id: string; name: string; url: string; latestVersionDetected: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaintenanceCustomer {
  id: string;
  name: string;
  notes: string | null;
  isActive: boolean;
  customerId: string | null;
  software: MaintenanceSoftware[];
  createdAt: string;
  updatedAt: string;
}

export interface SoftwareConfirmation {
  id: string;
  confirmedAt: string;
  user: { name: string; email: string } | null;
  fromVersion: string | null;
  toVersion: string;
  softwareName: string;
  customerName: string | null;
}

interface MaintenancePickerCustomer {
  id: string;
  name: string;
  customerNumber?: string | null;
  _count: { contacts: number };
}

export function createMaintenanceApi(request: RequestFn) {
  return {
    // ── Kunden-Hub ────────────────────────────────────────────────────────────
    getAvailableHubCustomers(): Promise<{ customers: MaintenancePickerCustomer[]; totalHubCustomers: number }> {
      return request('/maintenance/available-hub-customers');
    },

    searchMaintenanceHubCustomers(
      search: string,
      page: number,
      limit: number
    ): Promise<{ customers: MaintenancePickerCustomer[]; hasMore: boolean }> {
      const params = new URLSearchParams({
        search,
        page: String(page),
        limit: String(limit),
      });
      return request(`/maintenance/available-hub-customers?${params}`);
    },

    // ── Wartungskunden ────────────────────────────────────────────────────────
    getMaintenanceCustomers(): Promise<{ customers: MaintenanceCustomer[] }> {
      return request('/maintenance/customers');
    },

    createMaintenanceCustomer(body: {
      name: string;
      notes?: string;
      isActive?: boolean;
      customerId?: string | null;
    }): Promise<MaintenanceCustomer> {
      return request('/maintenance/customers', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    updateMaintenanceCustomer(
      id: string,
      body: { name: string; notes?: string; isActive?: boolean; customerId?: string | null }
    ): Promise<MaintenanceCustomer> {
      return request(`/maintenance/customers/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    deleteMaintenanceCustomer(id: string): Promise<{ ok: boolean }> {
      return request(`/maintenance/customers/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    // ── Software ──────────────────────────────────────────────────────────────
    createMaintenanceSoftware(
      customerId: string,
      body: {
        name: string;
        currentVersion?: string | null;
        notes?: string | null;
        updateSourceId?: string | null;
      }
    ): Promise<MaintenanceSoftware> {
      return request(`/maintenance/customers/${encodeURIComponent(customerId)}/software`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    updateMaintenanceSoftware(
      id: string,
      body: {
        name: string;
        currentVersion?: string | null;
        notes?: string | null;
        updateSourceId?: string | null;
      }
    ): Promise<MaintenanceSoftware> {
      return request(`/maintenance/software/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    confirmSoftwareInstalled(id: string): Promise<MaintenanceSoftware> {
      return request(`/maintenance/software/${encodeURIComponent(id)}/confirm-installed`, { method: 'PATCH' });
    },

    deleteMaintenanceSoftware(id: string): Promise<{ ok: boolean }> {
      return request(`/maintenance/software/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    // ── Update-Quellen ────────────────────────────────────────────────────────
    getUpdateSources(): Promise<{ sources: UpdateSource[] }> {
      return request('/maintenance/sources');
    },

    createUpdateSource(body: {
      name: string;
      url: string;
      description?: string | null;
      checkIntervalHours?: number | null;
      checkMethod?: UpdateSourceCheckMethod;
      loginUrl?: string | null;
      loginUsername?: string | null;
      loginPassword?: string | null;
    }): Promise<UpdateSource> {
      return request('/maintenance/sources', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    updateUpdateSource(
      id: string,
      body: {
        name: string;
        url: string;
        description?: string | null;
        checkIntervalHours?: number | null;
        checkMethod?: UpdateSourceCheckMethod;
        loginUrl?: string | null;
        loginUsername?: string | null;
        loginPassword?: string | null;
      }
    ): Promise<UpdateSource> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    testSourceLogin(id: string): Promise<{ ok: boolean; error: string | null }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}/test-login`, { method: 'POST' });
    },

    deleteUpdateSource(id: string): Promise<{ ok: boolean }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    checkUpdateSource(id: string): Promise<{ version: string | null; error: string | null; source: UpdateSource }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}/check`, { method: 'POST' });
    },

    recordManualCheckResult(id: string, version: string | null): Promise<{ source: UpdateSource }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}/manual-result`, {
        method: 'POST',
        body: JSON.stringify({ version }),
      });
    },

    getUpdateCheckLogs(id: string): Promise<{ logs: UpdateCheckLog[] }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}/logs`);
    },

    previewSource(id: string): Promise<{ base64Png: string }> {
      return request(`/maintenance/sources/${encodeURIComponent(id)}/preview`, { method: 'POST' });
    },

    getSoftwareConfirmations(id: string): Promise<{ confirmations: SoftwareConfirmation[] }> {
      return request(`/maintenance/software/${encodeURIComponent(id)}/confirmations`);
    },
  };
}
